import React, { useState, useEffect } from 'react'
import { Box, Stack, CircularProgress, List } from '@mui/material'
import getAgent from '@plug-core/utils/getAgent'
import useWallet from '@plug-core/hooks/useWallet'
import EmptyList from '@components/EmptyList'
import { useTranslation } from 'react-i18next'
import OneSecClient from '@plug-core/clients/OneSecClient'
import { Transfer, Status } from '@plug-core/idls/onesec/onesec.did'
import useConfig from '@plug-core/hooks/useConfig'
import { TransactionListItem } from '@components/TransactionList/TransactionListItem'
import { TransactionActivity, TransactionStatus } from '@plug-core/interfaces/transactionActivity'
import { TransactionKind } from '@plug-core/clients/LedgerScannerClient'
import { Titlebar } from '@components/Titlebar/Titlebar'
import { useNavigate } from 'react-router-dom'
import { getFirstKey } from '@plug-core/utils/getFirstKey'

const getStatus = (status: [Status] | []): TransactionStatus => {
  if (status.length === 0) {
    return TransactionStatus.Confirmed
  }

  const s = status[0]

  if ('Failed' in s) {
    return TransactionStatus.Failed
  } else if ('PendingRefundTx' in s || 'PendingDestinationTx' in s || 'PendingSourceTx' in s) {
    return TransactionStatus.Pending
  } else {
    return TransactionStatus.Confirmed
  }
}

export const BridgeHistory = () => {
  const [history, setHistory] = useState<Transfer[]>([])
  const [loading, setLoading] = useState(true)

  const { tokens } = useConfig()
  const navigate = useNavigate()
  const { wallet } = useWallet()
  const { t } = useTranslation()

  const loadHistory = async () => {
    setLoading(true)

    const agent = getAgent()
    const topUp = new OneSecClient(agent)
    const historyData = await topUp.fetchHistory(wallet.principalId, wallet.ethAddress!)

    if ('Ok' in historyData) {
      setHistory(historyData.Ok)
    }
    setLoading(false)
  }

  useEffect(() => {
    loadHistory()
  }, [wallet.ethAddress])

  return (
    <Box
      mx={2}
      mt={2}
      sx={{
        '&::-webkit-scrollbar': {
          display: 'none',
        },
        msOverflowStyle: 'none',
        scrollbarWidth: 'none',
        overflow: 'auto',
      }}
    >
      <Titlebar
        title={t('onesec.bridgeHistory')}
        onActionClick={{
          type: 'back',
          action: () => navigate(-1),
        }}
        refreshAction={loadHistory}
        isRefreshing={loading}
      />
      {loading ? (
        <Stack alignItems="center" mt={5}>
          <CircularProgress />
        </Stack>
      ) : (
        <Stack component={List} spacing={2}>
          {history.length ? (
            history
              .sort((a, b) => {
                const aStart = a.start?.[0] ? Number(a.start[0]) : 0
                const bStart = b.start?.[0] ? Number(b.start[0]) : 0
                return bStart - aStart
              })
              .map((tx, index) => {
                const tokenId = OneSecClient.oneSecTokenToPlugToken(
                  getFirstKey(tx.destination.token),
                  getFirstKey(tx.destination.chain)
                )

                if (tokenId) {
                  const plugToken = tokens.find((t) => t.id === tokenId)
                  if (plugToken) {
                    const transaction: TransactionActivity = {
                      chain: plugToken.chain,
                      kind: TransactionKind.Transfer,
                      senderAddress: 'OneSec',
                      receiverAddress: wallet.principalId,
                      memo: null,
                      amount: Number(tx.destination.amount),
                      type: 'receive',
                      timestamp: new Date(Number(tx.start[0])),
                      tokenLedgerId: plugToken.address || '',
                      status: getStatus(tx.status),
                      transactionId: index,
                      logo: plugToken.logo,
                      decimals: plugToken.decimals,
                    }

                    return (
                      <TransactionListItem
                        key={transaction.transactionId}
                        transaction={transaction}
                      />
                    )
                  }
                }
              })
          ) : (
            <EmptyList title={t('topupHistory.empty')} />
          )}
        </Stack>
      )}
    </Box>
  )
}
